import API from '@/interceptors/Api';
import type { PaginationResponseType } from "@/features/shared/hooks/hooks/pagination.type";
import type { OwnerWithRules, OwnerOrderBy, Owner } from "../types/owner.types";

/**
 * Search owners by name/fiscal number and return only their fiscal numbers.
 * Used by TransferAccountAutocomplete to discover accounts of matching owners.
 *
 * GET /cash-pooling/rules/owners?search={search}&page=0&size=20
 */
export const searchOwnerFiscalNumbers = async (
  search: string,
): Promise<string[]> => {
  const response = await API.get<
    PaginationResponseType<{ owner: Owner }>
  >('/cash-pooling/rules/owners', {
    params: { search, page: 0, size: 20 },
  });
  const apiResponse = response as unknown as PaginationResponseType<{ owner: Owner }>;
  return apiResponse.content.map((item) => item.owner.fiscalNumber);
};

/**
 * Request parameters for fetching owners
 */
export interface GetOwnersRequest {
  search?: string;
  orderBy?: OwnerOrderBy;
  page?: number;
  size?: number;
}

/**
 * Institution with rules summary
 */
interface InstitutionWithRulesSummary {
  name: string;
  logo: string;
  description: string;
}

/**
 * Rule summary with enabled/disabled counts
 */
interface RuleSummaryItem {
  enabled: number;
  disabled: number;
}

/**
 * API response structure for owners endpoint
 */
interface OwnerApiResponse {
  owner: Owner;
  rulesSummary: {
    excess: RuleSummaryItem;
    minimum: RuleSummaryItem;
    fixed?: RuleSummaryItem;
  };
  institutionWithRulesSummary: InstitutionWithRulesSummary[];
}

/**
 * GET: /api/v1/cash-pooling/rules/owners
 * Returns paginated owners with rules count
 */
export const getOwners = async (
  params: GetOwnersRequest = {},
): Promise<PaginationResponseType<OwnerWithRules>> => {
  const { search, page, size } = params;
  const queryParams = new URLSearchParams();

  if (search) {
    queryParams.append('search', search);
  }

  if (page !== undefined) {
    queryParams.append('page', page.toString());
  }

  if (size !== undefined) {
    queryParams.append('size', size.toString());
  }

  const url = `/cash-pooling/rules/owners`;

  const response = await API.get<PaginationResponseType<OwnerApiResponse>>(url, {
    params: {
      search,
      page,
      size,
    },
  });
  const apiResponse = response as unknown as PaginationResponseType<OwnerApiResponse>;

  // Map API response to expected structure

  return {
    ...apiResponse,
    content: apiResponse.content.map((item) => {
      return {
        owner: item.owner,
        rulesCount: {
          excess: item.rulesSummary.excess ? {
            enabled: item.rulesSummary.excess.enabled,
            disabled: item.rulesSummary.excess.disabled,
          } : { enabled: 0, disabled: 0 },
          shortage: item.rulesSummary.minimum ? {
            enabled: item.rulesSummary.minimum.enabled,
            disabled: item.rulesSummary.minimum.disabled,
          } : { enabled: 0, disabled: 0 },
          fixed: item.rulesSummary.fixed
            ? {
              enabled: item.rulesSummary.fixed.enabled,
              disabled: item.rulesSummary.fixed.disabled,
            }
            : { enabled: 0, disabled: 0 },
        },
        banksWithRules: item.institutionWithRulesSummary.map((institution) => ({
          id: institution.name,
          name: institution.name,
          description: institution.description,
          logo: institution.logo,
        })),
      };
    }),
  };
};
